import { HappyReturnsBillingMode } from "@redotech/cockroachdb-codegen/congodb/happy-returns-billing-mode";
import { HappyReturnsDropoffMethod } from "@redotech/cockroachdb-codegen/congodb/happy-returns-dropoff-method";
import { Currency } from "@redotech/money/currencies";
import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const happyReturnsPricing = z.object({
  feesByDropoffMethod: z
    .array(
      z.object({
        dropoffMethod: createEnum(HappyReturnsDropoffMethod),
        fee: z.object({
          amount: z.number().nonnegative(),
          currency: z.literal(Currency.USD),
        }),
      }),
    )
    .superRefine((fees, ctx) => {
      const configuredMethods = new Set<HappyReturnsDropoffMethod>();
      for (const [index, { dropoffMethod }] of fees.entries()) {
        if (configuredMethods.has(dropoffMethod)) {
          ctx.addIssue({
            code: "custom",
            message: "Each drop-off method can only have one fee",
            path: [index, "dropoffMethod"],
          });
        }
        configuredMethods.add(dropoffMethod);
      }
    }),
});

export const getHappyReturnsIntegration = {
  name: "getHappyReturnsIntegration" as const,
  input: z.object({}),
  output: z.object({
    happyReturnsIntegration: z
      .object({
        enabled: z.boolean(),
        configurationComplete: z.boolean(),
        retailerId: z.string(),
      })
      .optional(),
  }),
};

export const getHappyReturnsAdminConfiguration = {
  name: "getHappyReturnsAdminConfiguration" as const,
  input: z.object({}),
  output: z.object({
    happyReturnsAdminConfiguration: z
      .object({
        billingMode: createEnum(HappyReturnsBillingMode),
        eligibleAutoProcessCompensationMethods: z.array(
          createEnum(CompensationMethod),
        ),
        enabledDropoffMethods: z.array(createEnum(HappyReturnsDropoffMethod)),
        pricing: happyReturnsPricing,
      })
      .optional(),
  }),
};

export const putHappyReturnsIntegration = {
  name: "putHappyReturnsIntegration" as const,
  input: z.object({
    billingMode: createEnum(HappyReturnsBillingMode).optional(),
    enabled: z.boolean(),
    eligibleAutoProcessCompensationMethods: z
      .array(createEnum(CompensationMethod))
      .optional(),
    enabledDropoffMethods: z
      .array(createEnum(HappyReturnsDropoffMethod))
      .optional(),
    pricing: happyReturnsPricing.optional(),
    retailerId: z.string().trim().min(1, "Retailer ID is required"),
  }),
  output: z.object({}),
};

export type PutHappyReturnsIntegrationInput = z.infer<
  typeof putHappyReturnsIntegration.input
>;
